import { createColumnHelper } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon } from "semantic-ui-react";

import { CategoryLicenseModal, DATE_FORMATS_FNS, ReactTable, useCan, useSelectedLocation } from "components";
import { typedQueryClient, useRegisterNavAction } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { LOCATION_LICENSE_SUBPAGE } from "modules/LocationLicenseOverview";
import { useItemsQuery } from "modules/LocationLicenseOverview/hooks";
import { getLicenseCategoryName } from "modules/LocationLicenseOverview/utils";
import "modules/UnlinkedExactLicensesDashboard/components/UnlinkedExactLicensesTable/UnlinkedExactLicensesTable.scss";
import { useIgnoreLicenseMutation, useUnlinkedLicensesQuery } from "modules/UnlinkedExactLicensesDashboard/hooks";
import { UnlinkedExactLicensesQueryKeys } from "modules/UnlinkedExactLicensesDashboard/queryKeys";
import { getUserRoleName } from "utils/common";
import { formatDate } from "utils/dateHelperFunctions";

import type { ExactCategory } from "models";
import type { LicenseType } from "modules/LocationLicenseOverview";
import type { Licenses } from "modules/LocationLicenseOverview/components";

const defaultLicenseData: LicenseType = {
  id: 0,
  categoryID: null,
  exact_item_id: "",
  code: "",
  name: null,
  category: null,
  quantity: 0,
  price: 0,
  description: null,
  dealer_location_name: null,
  totalPrice: 0,
  invoiceOn: null,
  role_id: null
};

const columnHelper = createColumnHelper<ExactCategory>();

const handleUpdateExactLicenses = (id: number) => {
  const unlinkedExactCategories = typedQueryClient.getQueryData(UnlinkedExactLicensesQueryKeys.unlinkedExactCategories);
  if (!unlinkedExactCategories) return;

  const exactCategoryIdx = unlinkedExactCategories.findIndex(ec => ec.id === id);
  if (exactCategoryIdx === -1) return;

  typedQueryClient.setQueryData(UnlinkedExactLicensesQueryKeys.unlinkedExactCategories, unlinkedExactCategories.toSpliced(exactCategoryIdx, 1));
};

const getIgnoreLicensePayload = ({ id, is_unlinked_license_ignored, role_id, license_category_type, dealer_location_id }: ExactCategory) => {
  if (id) return { exact_category_id: id, ignore: !is_unlinked_license_ignored };

  return { ignore: !is_unlinked_license_ignored, role_id, exact_category_type: license_category_type, dealer_location_id };
};

export const UnlinkedExactLicensesTable = () => {
  const { t } = useTranslation();

  const { items } = useItemsQuery();
  const { mutate: ignoreLicense } = useIgnoreLicenseMutation();
  const { selectedLocation } = useSelectedLocation();
  const canIgnoreLicense = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.ExactLicenseCategory);
  const { data: exactCategories = [], isFetching: isFetchingUnlinkedLicenses, refetch: refetchUnlinkedLicenses } = useUnlinkedLicensesQuery();
  const [licenses, setLicenses] = useState<Licenses>({ monthlyCategories: [], oneTimeCategories: [], monthlyStandalones: [], oneTimeStandalones: [] });

  const [isCategoryLicenseModalOpen, setIsCategoryLicenseModalOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<LicenseType | null>(null);
  const [currentLocationLicenseSubpage, setCurrentLocationLicenseSubpage] = useState<LOCATION_LICENSE_SUBPAGE>(LOCATION_LICENSE_SUBPAGE.OneTimeLicenses);

  useRegisterNavAction(refetchUnlinkedLicenses);

  const handleRowClick = (exactCategory: ExactCategory | null) => {
    if (!exactCategory?.license_category_type) return;

    setSelectedLicense({
      ...defaultLicenseData,
      categoryID: Number(exactCategory.id),
      category: String(exactCategory.license_category_type),
      quantity: exactCategory.quantity,
      role_id: exactCategory.role_id,
      ...exactCategory
    });
    setCurrentLocationLicenseSubpage(exactCategory.one_time ? LOCATION_LICENSE_SUBPAGE.OneTimeLicenses : LOCATION_LICENSE_SUBPAGE.MonthlyLicenses);

    const { monthlyCategories, oneTimeCategories } = exactCategories.reduce<Licenses>(
      (acc, item) => {
        if (item.one_time) acc.oneTimeCategories.push(item);
        else acc.monthlyCategories.push(item);
        return acc;
      },
      { monthlyCategories: [], oneTimeCategories: [], monthlyStandalones: [], oneTimeStandalones: [] }
    );

    setLicenses(prev => ({
      ...prev,
      monthlyCategories,
      oneTimeCategories,
      [exactCategory.one_time ? "oneTimeCategories" : "monthlyCategories"]: [exactCategory]
    }));

    setIsCategoryLicenseModalOpen(true);
  };

  const handleIgnoreLicense = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, exactCategory: ExactCategory) => {
    e.stopPropagation();
    ignoreLicense(getIgnoreLicensePayload(exactCategory));
  };

  const getCategoryType = ({ role_id, license_category_type }: ExactCategory) =>
    `${getLicenseCategoryName(license_category_type, t)} ${role_id ? `(${getUserRoleName(t, role_id)})` : ""}`;

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "dealers",
        header: () => <span className="header-title">{t("v8_dealer", "Dealer")}</span>,
        size: 250,
        cell: ({ row: { original: exactCategory } }) => <span className="text-ellipsis">{exactCategory.dealer_name}</span>
      }),

      columnHelper.accessor(row => row, {
        id: "locations",
        header: () => <span className="header-title"> {t("v8_location", "Location")} </span>,
        size: 200,
        cell: ({ row: { original: exactCategory } }) => <span className="text-ellipsis">{exactCategory.dealer_location_name}</span>
      }),

      columnHelper.accessor(row => row, {
        id: "license-category-name",
        header: () => <span className="header-title">{t("v8_role_license_service", "Role / License / Service")}</span>,
        size: 450,
        cell: ({ row: { original: exactCategory } }) => <span className="text-ellipsis">{getCategoryType(exactCategory)}</span>
      }),

      columnHelper.accessor(row => row, {
        id: "amount",
        header: () => <span className="header-title">{t("v8_amount", "Amount")}</span>,
        size: 100,
        cell: ({ row: { original: exactCategory } }) => <span className="text-ellipsis">{exactCategory.quantity}</span>
      }),

      columnHelper.accessor(row => row, {
        id: "last-updated-by",
        header: () => <span className="header-title">{t("v8_last_updated_by", "Last updated by")}</span>,
        size: 180,
        cell: ({ row: { original: exactCategory } }) => {
          if (!exactCategory.is_unlinked_license_ignored_updated_by) return;

          return (
            <span className="text-ellipsis">
              {t(
                exactCategory.is_unlinked_license_ignored ? "v8_ignored_by_user_on_date" : "v8_unignored_by_user_on_date",
                exactCategory.is_unlinked_license_ignored ? "Ignored by {{user}} on {{date}}" : "Unignored by {{user}} on {{date}}",
                {
                  user: exactCategory.is_unlinked_license_ignored_updated_by,
                  date: formatDate(exactCategory.is_unlinked_license_ignored_updated_on || "", DATE_FORMATS_FNS.dateMonthYearTimeWithSeconds)
                }
              )}
            </span>
          );
        }
      }),

      columnHelper.accessor(row => row, {
        id: "actions",
        header: () => <span className="header-title">{t("v8_ignore_license", "Ignore License")}</span>,
        size: 50,
        cell: ({ row: { original: exactCategory } }) => (
          <Button
            size="small"
            className={exactCategory.is_unlinked_license_ignored ? "" : "ignore"}
            disabled={!canIgnoreLicense}
            onClick={e => handleIgnoreLicense(e, exactCategory)}
          >
            <Icon className="flag" />
            {exactCategory.is_unlinked_license_ignored ? t("v8_ignored", "Ignored") : t("v8_ignore", "Ignore")}
          </Button>
        )
      })
    ],
    [t, handleIgnoreLicense, getCategoryType, canIgnoreLicense]
  );

  return (
    <div className="UnlinkedExactLicensesTable">
      <ReactTable isFetching={isFetchingUnlinkedLicenses} columns={columns} data={exactCategories} onRowClick={handleRowClick} />
      <CategoryLicenseModal
        items={items}
        isOpen={isCategoryLicenseModalOpen}
        selectedTab={currentLocationLicenseSubpage}
        licensesData={licenses}
        selectedLicense={selectedLicense}
        locationId={selectedLocation?.id || 0}
        isLoadingLicenses={isFetchingUnlinkedLicenses}
        onClose={() => setIsCategoryLicenseModalOpen(false)}
        onUpdateExactLicenses={handleUpdateExactLicenses}
      />
    </div>
  );
};
